import { useNavigate } from 'react-router-dom';
import { useOutboxStore, esDelComercio } from '../store/outboxStore.js';
import { useAuthStore } from '../store/authStore.js';
import { useConfirmacion } from '../components/ConfirmSheet.jsx';
import styles from './PendingScreen.module.css';

// Lo que este aparato todavía no ha logrado entregar.
//
// Existe porque el badge del encabezado da un número y nada más: "2 órdenes
// sin subir" no le dice al mesero qué mesa, ni si puede hacer algo. Aquí se ve
// el contenido —para poder recapturarlo si hace falta— y qué dijo el servidor.

const hora = (iso) => {
  try { return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const Platillos = ({ items = [] }) => (
  <ul className={styles.platillos}>
    {items.map((i, n) => (
      <li key={n}>
        <span className={styles.cant}>{i.quantity}×</span> {i.name}
        {(i.modifiers ?? []).length > 0 && (
          <small> · {i.modifiers.map((m) => m.option_name).join(', ')}</small>
        )}
      </li>
    ))}
  </ul>
);

export default function PendingScreen() {
  const { pedir, Confirmacion } = useConfirmacion();
  const navigate  = useNavigate();
  // Solo lo que se capturó en el comercio en el que se está.
  const comercio  = useAuthStore((s) => (s.tenantId ? String(s.tenantId) : null));
  const deAqui    = (e) => esDelComercio(e, comercio);
  const comandas  = useOutboxStore((s) => s.comandas).filter(deAqui);
  const fallidas  = useOutboxStore((s) => s.fallidas ?? []).filter(deAqui);
  const cuentas   = useOutboxStore((s) => s.pendientes);
  const drenar    = useOutboxStore((s) => s.drenar);
  const reintentar = useOutboxStore((s) => s.reintentarComanda);
  const descartar  = useOutboxStore((s) => s.descartarComanda);

  const cuentasPend = Object.values(cuentas).filter(deAqui);
  const vacio = !comandas.length && !fallidas.length && !cuentasPend.length;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
        <h1 className={styles.title}>Sin enviar</h1>
        {!vacio && (
          <button className="btn btn--ghost" onClick={() => drenar().catch(() => {})}>
            Intentar ahora
          </button>
        )}
      </div>

      {vacio && (
        <p className={styles.vacio}>
          Todo entregado. Cuando algo no alcance a salir —porque se fue la señal o porque no hay
          caja abierta— aparece aquí y se manda solo en cuanto se pueda.
        </p>
      )}

      {/* En espera: no hay nada que hacer, se van solas. Se listan para que el
          mesero sepa que su orden no se perdió. */}
      {comandas.length > 0 && (
        <section className={styles.bloque}>
          <h2 className={styles.subtitulo}>En espera · {comandas.length}</h2>
          <p className={styles.hint}>Se mandan solas. No hay que hacer nada.</p>
          {comandas.map((c, i) => (
            <article key={i} className={styles.tarjeta}>
              <div className={styles.tarjetaTop}>
                <strong>{c.payload.tab_name ?? 'Sin mesa'}</strong>
                <span className={styles.meta}>{c.payload.station} · {hora(c.since)}</span>
              </div>
              <Platillos items={c.payload.items} />
              {c.lastError && <p className={styles.espera}>{c.lastError}</p>}
            </article>
          ))}
        </section>
      )}

      {/* Apartadas: aquí sí hay una decisión humana que tomar. */}
      {fallidas.length > 0 && (
        <section className={styles.bloque}>
          <h2 className={styles.subtitulo}>Apartadas · {fallidas.length}</h2>
          <p className={styles.hint}>
            Estas no entraron. Revisa si la cocina las recibió por otro lado antes de reintentar.
          </p>
          {fallidas.map((c, i) => (
            <article key={i} className={`${styles.tarjeta} ${styles.tarjetaMala}`}>
              <div className={styles.tarjetaTop}>
                <strong>{c.payload.tab_name ?? 'Sin mesa'}</strong>
                <span className={styles.meta}>{c.payload.station} · {hora(c.since)}</span>
              </div>
              <Platillos items={c.payload.items} />
              <p className={styles.error}>{c.lastError}</p>
              <div className={styles.acciones}>
                <button className="btn btn--primary" onClick={() => reintentar(c)}>Reintentar</button>
                <button
                  className="btn btn--ghost"
                  onClick={async () => {
                    if (await pedir({ titulo: '¿Descartar esta comanda?', texto: 'Se pierde. Solo si la cocina ya la tiene por otro lado.', confirmar: 'Descartar', peligro: true })) descartar(c);
                  }}
                >
                  Descartar
                </button>
              </div>
            </article>
          ))}
        </section>
      )}

      {cuentasPend.length > 0 && (
        <section className={styles.bloque}>
          <h2 className={styles.subtitulo}>Cuentas por guardar · {cuentasPend.length}</h2>
          <p className={styles.hint}>
            Los platillos están anotados en este aparato y suben solos. Otra tablet todavía no los ve.
          </p>
          {cuentasPend.map((c) => (
            <article key={c.tabId} className={styles.tarjeta}>
              <div className={styles.tarjetaTop}>
                <strong>{c.tabId}</strong>
                <span className={styles.meta}>{c.items.length} renglones · {hora(c.since)}</span>
              </div>
              {c.lastError && <p className={styles.espera}>{c.lastError}</p>}
            </article>
          ))}
        </section>
      )}
      {Confirmacion}
    </div>
  );
}
